import { useState, useLayoutEffect } from 'react';
import {
  Editor,
  EditorState,
  convertToRaw,
  convertFromRaw,
  RichUtils,
  ContentState,
  Modifier,
  SelectionState,
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';

import {
  Pen,
  TextEditorBold,
  TextEditorItalic,
  TextEditorUnderline,
  TextEditorClearStyles,
} from 'assets/icons';
import { IconButton, Loader, Buttons } from 'components';
import { manageCampaignsAPI } from 'services/manageCampaigns.service';

import 'draft-js/dist/Draft.css';
import * as S from './NotesContent.styled';

export const NotesContent = ({ note, selectedDate, isNotesError }) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [displayHtml, setDisplayHtml] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEdited, setIsEdited] = useState(false);

  const [onSaveEventNote, saveResult] = manageCampaignsAPI.useSaveCampaignEventMutation();

  useLayoutEffect(() => {
    try {
      if (note && !saveResult.isLoading) {
        setLoading(true);
        const rawContent = JSON.parse(note);
        const contentStateFromRaw = convertFromRaw(rawContent);

        setEditorState(EditorState.createWithContent(contentStateFromRaw));

        const html = stateToHTML(contentStateFromRaw);
        setDisplayHtml(html);
        setLoading(false);
      }
    } catch {
      setLoading(false);
      // handle case when a note come as a plain text
      if (note && typeof note === 'string') {
        const contentState = ContentState.createFromText(note.trim());
        const editorState = EditorState.createWithContent(contentState);
        const html = stateToHTML(contentState);
        setDisplayHtml(html);
        setEditorState(editorState);
      } else {
        setDisplayHtml('');
      }
    }
    setIsEdited(false);
  }, [note, isEditorOpen]);

  function toggleInlineStyle(inlineStyle) {
    setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  }

  function handleSave() {
    // convert editor content to raw JSON and html
    const contentState = editorState.getCurrentContent();
    const isNotEmpty = contentState?.getPlainText()?.trim()?.length;

    let jsonString = '';

    if (isNotEmpty) {
      const html = stateToHTML(contentState);
      const rawContentState = convertToRaw(contentState);
      jsonString = JSON.stringify(rawContentState);

      // force reseting state even though save can fail
      setDisplayHtml(html);
    } else {
      setDisplayHtml('');
    }

    setIsEditorOpen(false);

    const formatedDate = getDateForRequest(selectedDate);
    onSaveEventNote({ noteDate: formatedDate, noteText: jsonString });
  }

  const clearAllStyles = () => {
    const styles = ['BOLD', 'ITALIC', 'UNDERLINE'];
    const contentState = editorState.getCurrentContent();
    const emptySelection = SelectionState.createEmpty(contentState.getFirstBlock().getKey());

    // create a new ContentState with all inline styles removed
    const blockMap = contentState.getBlockMap();
    let newContentState = contentState;

    blockMap.forEach((block, blockKey) => {
      const blockSelection = emptySelection.merge({
        anchorKey: blockKey,
        focusKey: blockKey,
        anchorOffset: 0,
        focusOffset: block.getLength(),
      });
      styles.forEach((style) => {
        newContentState = Modifier.removeInlineStyle(newContentState, blockSelection, style);
      });
    });

    const newEditorState = EditorState.push(editorState, newContentState, 'change-inline-style');
    setEditorState(EditorState.forceSelection(newEditorState, emptySelection));
  };

  function handleEditorChange(state) {
    if (editorState.getCurrentContent() !== state.getCurrentContent()) {
      setIsEdited(true);
    }
    setEditorState(state);
  }

  if (isNotesError || saveResult.isError) {
    return (
      <>
        <S.PaneHeader>
          <S.PaneTitle>Note:</S.PaneTitle>
        </S.PaneHeader>
        <S.NoteText>Error: loading failed.</S.NoteText>
      </>
    );
  }

  return (
    <>
      <S.PaneHeader>
        <S.PaneTitle>Note:</S.PaneTitle>
        {!isEditorOpen && (
          <IconButton.SmallSize
            isDefaultMuted
            icon={Pen}
            type={IconButton.btnTypes.tertiary}
            onClick={() => setIsEditorOpen(true)}
          />
        )}
      </S.PaneHeader>
      {(saveResult.isLoading || loading) && <Loader.Local delay={0} />}
      {isEditorOpen && !saveResult.isLoading && !loading && (
        <>
          <S.EditorRoot>
            <S.EditorHeader>
              <S.Button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsEdited(true);
                  toggleInlineStyle('BOLD');
                }}
              >
                <TextEditorBold />
              </S.Button>
              <S.Button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsEdited(true);
                  toggleInlineStyle('ITALIC');
                }}
              >
                <TextEditorItalic />
              </S.Button>
              <S.Button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsEdited(true);
                  toggleInlineStyle('UNDERLINE');
                }}
              >
                <TextEditorUnderline />
              </S.Button>
              <S.Button
                style={{ marginLeft: 'auto' }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  clearAllStyles();
                }}
              >
                <TextEditorClearStyles />
              </S.Button>
            </S.EditorHeader>
            <S.Editor className='editor'>
              <Editor editorState={editorState} onChange={handleEditorChange} />
            </S.Editor>
          </S.EditorRoot>
          <S.PaneFooter>
            <Buttons.PrimarySmall id='SaveNote' isDisabled={!isEdited} onClick={handleSave}>
              Save Note
            </Buttons.PrimarySmall>
            <Buttons.SecondarySmall id='Cancel' onClick={() => setIsEditorOpen(false)}>
              Cancel
            </Buttons.SecondarySmall>
          </S.PaneFooter>
        </>
      )}
      {!isEditorOpen && !saveResult.isLoading && !loading && (
        <>
          {displayHtml ? (
            <S.NoteText dangerouslySetInnerHTML={{ __html: displayHtml }}></S.NoteText>
          ) : (
            <S.NoteText>Type your notes here.</S.NoteText>
          )}
        </>
      )}
    </>
  );
};

function getDateForRequest(selectedDate) {
  const year = selectedDate.getFullYear();
  const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
  const day = String(selectedDate.getDate()).padStart(2, '0');
  const hours = String(selectedDate.getHours()).padStart(2, '0');
  const minutes = String(selectedDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

NotesContent.displayName = 'NotesContent';
